import { useEffect, useRef, useState } from "react";
import { Camera, Upload, Link2, Loader2, ScanLine, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const TABS = [
  { id: "camera", label: "Fotocamera", icon: Camera },
  { id: "upload", label: "Carica file", icon: Upload },
  { id: "url", label: "Pagina web", icon: Link2 },
];

const CameraBox = ({ onShot }) => {
  const video = useRef(null);
  const [stream, setStream] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let s;
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "environment" } })
      .then((st) => { s = st; setStream(st); if (video.current) video.current.srcObject = st; })
      .catch(() => setErr("Fotocamera non disponibile. Usa lo scatto dal dispositivo."));
    return () => s?.getTracks().forEach((t) => t.stop());
  }, []);

  const shoot = () => {
    const v = video.current;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((b) => onShot(new File([b], "scansione.jpg", { type: "image/jpeg" })), "image/jpeg", 0.92);
  };

  return (
    <div className="space-y-4">
      <div className="relative aspect-video bg-zinc-950 overflow-hidden">
        {stream ? <video ref={video} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="camera-video" /> :
          <div className="absolute inset-0 grid place-items-center text-zinc-400 text-sm px-6 text-center">{err || "Avvio fotocamera…"}</div>}
        <div className="absolute inset-6 border border-white/40 pointer-events-none">
          {["top-0 left-0 border-t-2 border-l-2", "top-0 right-0 border-t-2 border-r-2", "bottom-0 left-0 border-b-2 border-l-2", "bottom-0 right-0 border-b-2 border-r-2"].map((k) => (
            <span key={k} className={`absolute w-6 h-6 border-[#0D9488] ${k}`} />
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-3">
        <button data-testid="camera-capture-btn" disabled={!stream} onClick={shoot} className="inline-flex items-center gap-2 h-11 px-5 bg-[#0D9488] text-white text-sm font-semibold hover:bg-[#059669] disabled:opacity-40 transition-colors duration-150">
          <ScanLine className="w-4 h-4" /> Scatta e scansiona
        </button>
        <label className="inline-flex items-center gap-2 h-11 px-5 border border-zinc-300 text-sm font-semibold cursor-pointer hover:border-zinc-950 transition-colors duration-150">
          <Camera className="w-4 h-4" /> Scatta dal dispositivo
          <input data-testid="camera-native-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => e.target.files[0] && onShot(e.target.files[0])} />
        </label>
      </div>
    </div>
  );
};

const UploadBox = ({ onFile }) => {
  const [drag, setDrag] = useState(false);
  return (
    <label
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); e.dataTransfer.files[0] && onFile(e.dataTransfer.files[0]); }}
      className={`flex flex-col items-center justify-center gap-3 h-72 border-2 border-dashed cursor-pointer transition-colors duration-150 ${drag ? "border-[#0D9488] bg-[#CCFBF1]" : "border-zinc-300 hover:border-zinc-950 bg-zinc-50"}`}
      data-testid="upload-dropzone"
    >
      <FileText className="w-10 h-10 text-zinc-400" strokeWidth={1.25} />
      <p className="font-display text-lg font-semibold">Trascina qui il documento</p>
      <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">JPG · PNG · PDF — max 15MB</p>
      <input data-testid="upload-file-input" type="file" accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf" className="hidden" onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
    </label>
  );
};

const UrlBox = ({ onUrl }) => {
  const [url, setUrl] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); url && onUrl(url); }} className="space-y-4">
      <p className="text-sm text-zinc-600">Incolla il link di un articolo, una voce di Wikipedia o una dispensa online.</p>
      <div className="flex flex-col sm:flex-row gap-3">
        <input data-testid="url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://it.wikipedia.org/wiki/Rivoluzione_francese" className="flex-1 h-12 px-4 border border-zinc-300 font-mono text-sm focus:outline-none focus:border-zinc-950" />
        <button data-testid="url-submit-btn" className="h-12 px-6 bg-[#0D9488] text-white text-sm font-semibold hover:bg-[#059669] transition-colors duration-150">Estrai testo</button>
      </div>
    </form>
  );
};

export const SourceInput = ({ onExtracted }) => {
  const [tab, setTab] = useState("upload");
  const [loading, setLoading] = useState("");

  const runFile = async (file, type) => {
    setLoading(`Aurora_OCR sta leggendo “${file.name}”…`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ocr/file", fd);
      if (!data.text) throw new Error("Nessun testo riconosciuto");
      onExtracted({ text: data.text, source_type: type, source_name: file.name, title: file.name.replace(/\.[^.]+$/, "") });
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(""); }
  };

  const runUrl = async (url) => {
    setLoading("Lettura della pagina web…");
    try {
      const { data } = await api.post("/ocr/url", { url });
      onExtracted({ text: data.text, source_type: "url", source_name: url, title: data.title || "Pagina web" });
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(""); }
  };

  return (
    <div data-testid="step-1-source-input">
      <div className="grid grid-cols-3 border border-zinc-200 mb-6">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`step-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center justify-center gap-2 h-14 text-sm font-semibold border-r last:border-r-0 border-zinc-200 transition-colors duration-150 ${tab === t.id ? "bg-[#0F3D3E] text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
            <t.icon className="w-4 h-4" /> <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>
      {loading ? (
        <div className="h-72 grid place-items-center border border-zinc-200 bg-zinc-50" data-testid="ocr-loading">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-[#0D9488]" />
            <p className="font-display font-semibold">{loading}</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-2">Estrazione ad alta precisione</p>
          </div>
        </div>
      ) : tab === "camera" ? <CameraBox onShot={(f) => runFile(f, "camera")} /> :
        tab === "upload" ? <UploadBox onFile={(f) => runFile(f, "upload")} /> : <UrlBox onUrl={runUrl} />}
    </div>
  );
};
